import React, { useState, useEffect } from 'react';
import { getLSAClient } from '../lib/google-lsa-client';
import RecordingPlayer from '../components/lsa/RecordingPlayer';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp,
  DollarSign,
  Phone,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Filter,
  Download,
  Flag,
  Star,
  Headphones,
  PhoneOff,
  PhoneCall,
  CalendarCheck,
} from 'lucide-react';

interface LSALead {
  id: string;
  google_lead_id: string;
  customer_name: string;
  customer_phone: string;
  service_type: string;
  status: string;
  lead_quality_score: number | null;
  charge_amount: number;
  response_time_seconds: number | null;
  booking_success: boolean | null;
  job_value: number | null;
  urgency_level: string;
  created_at: string;
  first_contact_at: string | null;
  // Contact Center fields
  contact_center_outcome?: string;
  contact_center_agent_name?: string;
  contact_center_recording_url?: string;
  contact_center_call_duration?: number;
  contact_center_disposition?: string;
  contact_center_notified?: boolean;
  // Job completion fields
  job_status?: string;
  job_completed_at?: string;
}

interface DashboardStats {
  today: {
    total: number;
    booked: number;
    cost: number;
  };
  month: {
    total: number;
    booked: number;
    cost: number;
    revenue: number;
  };
  budget: any;
  responsiveness_score: number;
}

export default function LSAManagerPage() {
  const { user, marketingUser } = useAuth();

  const companyId = marketingUser?.company_ids?.[0] || 'halesac';

  const [leads, setLeads] = useState<LSALead[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<LSALead | null>(null);

  const lsaClient = getLSAClient();

  useEffect(() => {
    loadData();
  }, [companyId, statusFilter, serviceFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load dashboard stats
      const dashboardStats = await lsaClient.getDashboardStats(companyId);
      setStats(dashboardStats);

      // Load leads with filters
      const filters: any = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (serviceFilter !== 'all') filters.service_type = serviceFilter;
      filters.limit = 100;

      const leadsData = await lsaClient.getLeads(companyId, filters);
      setLeads(leadsData as any);
    } catch (error) {
      console.error('Error loading LSA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkContacted = async (leadId: string) => {
    try {
      await lsaClient.updateLeadStatus(leadId, 'contacted');
      loadData();
    } catch (error) {
      console.error('Error marking as contacted:', error);
    }
  };

  const handleMarkBooked = async (leadId: string) => {
    const jobValue = prompt('Enter job value:');
    if (!jobValue) return;

    try {
      const jobId = `JOB_${Date.now()}`; // Simplified
      await lsaClient.updateLeadStatus(leadId, 'booked');
      alert('Lead marked as booked successfully');
      loadData();
    } catch (error) {
      console.error('Error marking as booked:', error);
    }
  };

  const handleFileDispute = async (lead: LSALead) => {
    const reason = prompt('Dispute reason (wrong_service, unreachable, duplicate, spam, out_of_area, commercial):');
    if (!reason) return;

    const description = prompt('Detailed description:');
    if (!description) return;

    try {
      const userId = user?.id || marketingUser?.id;

      if (!userId) {
        alert('Not authenticated. Please sign in again.');
        return;
      }
      
      await lsaClient.fileDispute({
        lead_id: lead.google_lead_id,
        reason: reason as any,
        description,
      }, companyId, userId);

      alert('Dispute filed successfully');
      loadData();
    } catch (error) {
      console.error('Error filing dispute:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; icon: any; label: string }> = {
      new: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'New' },
      contacted: { color: 'bg-yellow-100 text-yellow-800', icon: Phone, label: 'Contacted' },
      qualified: { color: 'bg-purple-100 text-purple-800', icon: Star, label: 'Qualified' },
      booked: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Booked' },
      invalid: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid' },
      disputed: { color: 'bg-orange-100 text-orange-800', icon: Flag, label: 'Disputed' },
    };

    const badge = badges[status] || badges.new;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'emergency': return 'text-red-600 font-bold';
      case 'high': return 'text-orange-600 font-semibold';
      case 'medium': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading LSA data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Local Services Ads Manager</h1>
          <p className="text-gray-600 mt-1">
            Manage LSA leads, budget, and responsiveness
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Leads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.today.total || 0}</p>
            </div>
            <Phone className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Booked</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats?.today.booked || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Spend</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${stats?.today.cost?.toFixed(2) || '0.00'}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-gray-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Month Revenue</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${stats?.month.revenue?.toLocaleString() || 0}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Responsiveness</p>
              <p className={`text-2xl font-bold mt-1 ${
                (stats?.responsiveness_score || 0) >= 90 ? 'text-green-600' :
                (stats?.responsiveness_score || 0) >= 75 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {stats?.responsiveness_score?.toFixed(0) || 0}%
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Budget Status */}
      {stats?.budget && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Budget Status</h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Daily</span>
                <span className="text-sm font-medium text-gray-900">
                  ${stats.budget.daily_spend?.toFixed(2)} / ${stats.budget.daily_budget?.toFixed(2)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    stats.budget.daily_usage_pct >= 100 ? 'bg-red-600' :
                    stats.budget.daily_usage_pct >= 80 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(100, stats.budget.daily_usage_pct)}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Weekly</span>
                <span className="text-sm font-medium text-gray-900">
                  ${stats.budget.weekly_spend?.toFixed(2)} / ${stats.budget.weekly_budget?.toFixed(2)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    stats.budget.weekly_usage_pct >= 100 ? 'bg-red-600' :
                    stats.budget.weekly_usage_pct >= 80 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(100, stats.budget.weekly_usage_pct)}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Monthly</span>
                <span className="text-sm font-medium text-gray-900">
                  ${stats.budget.monthly_spend?.toFixed(2)} / ${stats.budget.monthly_budget?.toFixed(2)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    stats.budget.monthly_usage_pct >= 100 ? 'bg-red-600' :
                    stats.budget.monthly_usage_pct >= 80 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(100, stats.budget.monthly_usage_pct)}%` }}
                />
              </div>
            </div>
          </div>

          {stats.budget.is_paused && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-800">
                  Lead acceptance paused: {stats.budget.pause_reason}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex gap-4 items-center">
          <Filter className="w-5 h-5 text-gray-500" />
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="booked">Booked</option>
            <option value="invalid">Invalid</option>
            <option value="disputed">Disputed</option>
          </select>

          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Services</option>
            <option value="AC Repair">AC Repair</option>
            <option value="AC Installation">AC Installation</option>
            <option value="AC Maintenance">AC Maintenance</option>
            <option value="Heating Repair">Heating Repair</option>
            <option value="Heating Installation">Heating Installation</option>
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">LSA Leads</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Call Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Recording
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Quality
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Response
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    No LSA leads found
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      {getStatusBadge(lead.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{lead.customer_name}</div>
                        <div className="text-gray-500">{lead.customer_phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{lead.service_type}</span>
                    </td>
                    {/* Call Status from Contact Center */}
                    <td className="px-6 py-4">
                      {lead.contact_center_outcome ? (
                        <div className="flex flex-col">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            lead.contact_center_outcome === 'booked' ? 'bg-green-100 text-green-800' :
                            lead.contact_center_outcome === 'missed' ? 'bg-red-100 text-red-800' :
                            lead.contact_center_outcome === 'callback_scheduled' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.contact_center_outcome === 'booked' && <CalendarCheck className="w-3 h-3" />}
                            {lead.contact_center_outcome === 'missed' && <PhoneOff className="w-3 h-3" />}
                            {lead.contact_center_outcome === 'not_booked' && <Phone className="w-3 h-3" />}
                            {lead.contact_center_outcome}
                          </span>
                          {lead.contact_center_agent_name && (
                            <span className="text-xs text-gray-400 mt-1">
                              {lead.contact_center_agent_name}
                            </span>
                          )}
                          {lead.contact_center_call_duration && (
                            <span className="text-xs text-gray-400">
                              {Math.floor(lead.contact_center_call_duration / 60)}:{String(lead.contact_center_call_duration % 60).padStart(2, '0')}
                            </span>
                          )}
                        </div>
                      ) : lead.contact_center_notified ? (
                        <span className="text-xs text-yellow-600 flex items-center gap-1">
                          <PhoneCall className="w-3 h-3" />
                          Awaiting call
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    {/* Recording Player */}
                    <td className="px-6 py-4">
                      <RecordingPlayer 
                        recordingUrl={lead.contact_center_recording_url || null}
                        callDuration={lead.contact_center_call_duration}
                        compact={true}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < (lead.lead_quality_score || 0)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        ${lead.charge_amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {lead.response_time_seconds ? (
                        <span className={`text-sm ${
                          lead.response_time_seconds < 60 ? 'text-green-600' :
                          lead.response_time_seconds < 300 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {Math.floor(lead.response_time_seconds / 60)}m
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {lead.booking_success ? (
                        <span className="text-sm font-medium text-green-600">
                          ${lead.job_value?.toLocaleString() || 0}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {lead.status === 'new' && (
                          <button
                            onClick={() => handleMarkContacted(lead.id)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Contact
                          </button>
                        )}
                        {(lead.status === 'new' || lead.status === 'contacted') && (
                          <button
                            onClick={() => handleMarkBooked(lead.id)}
                            className="text-sm text-green-600 hover:text-green-800"
                          >
                            Book
                          </button>
                        )}
                        {lead.status !== 'disputed' && lead.status !== 'invalid' && (
                          <button
                            onClick={() => handleFileDispute(lead)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Dispute
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Month Performance Summary */}
      {stats?.month && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Month Performance</h2>
          <div className="grid grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.month.total}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Booked</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.month.booked}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Spend</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${stats.month.cost.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Booking Rate</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {stats.month.total > 0 ? ((stats.month.booked / stats.month.total) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
